import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, Send, FileText, User } from 'lucide-react';
import { Client } from '@/types/client';
import { emailService } from '@/services/emailService';
import { StatusReportData } from '@/types/email';
import { useToast } from '@/hooks/use-toast';

interface EmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: Client | null;
  fiscalYear: string;
  quarter: string;
}

export const EmailDialog = ({ 
  open, 
  onOpenChange, 
  client, 
  fiscalYear, 
  quarter 
}: EmailDialogProps) => {
  const { toast } = useToast();
  const [isSending, setIsSending] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    body: '',
    includeStatusReport: true,
    includeAttachments: false
  });

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open && client) {
      const subject = `Tax Ready Status Report - ${client.companyName} - ${fiscalYear}${quarter !== 'all' ? ` - ${quarter}` : ''}`;
      setEmailData({
        to: client.emailAddress,
        subject,
        body: generateDefaultEmailBody(client, fiscalYear, quarter),
        includeStatusReport: true,
        includeAttachments: false
      });
    }
  }, [open, client, fiscalYear, quarter]);

  const generateDefaultEmailBody = (client: Client, fiscalYear: string, quarter: string): string => {
    const formatDate = (date: Date) => date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    return `
Dear ${client.directorName},

Please find below your Tax Ready status report for ${fiscalYear}${quarter !== 'all' ? ` - ${quarter}` : ''}.

===========================================
TAX READY STATUS REPORT
===========================================

Client: ${client.companyName}
Period: ${fiscalYear}${quarter !== 'all' ? ` - ${quarter}` : ''}
Report Generated: ${formatDate(new Date())}

TASK STATUS SUMMARY:
--------------------
BAS Status: ${client.basStatus}
Bookkeeping Status: ${client.bookkeepingStatus}
Payroll Status: ${client.payrollPayslip}
Super Payment Status: ${client.superPayment}
Invoices Status: ${client.invoices}

REMARKS:
--------
${client.remarks || 'No additional remarks.'}

===========================================
This is an automated status report from Tax Ready.
If you have any questions or need assistance, 
please don't hesitate to contact our team.

Thank you for choosing Tax Ready!
===========================================
    `.trim();
  };

  const handleSendEmail = async () => {
    if (!client) return;

    setIsSending(true);
    try {
      const result = await emailService.sendEmail({
        to: [{ email: emailData.to, name: client.directorName }],
        subject: emailData.subject,
        body: emailData.body,
        isHtml: false
      });

      if (result.success) {
        toast({
          title: 'Email Opened',
          description: result.message || 'Email has been opened in your default email client.',
        });
        onOpenChange(false);
      } else {
        toast({
          title: 'Email Failed',
          description: result.error || 'Failed to send email.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Email Error',
        description: 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleSendStatusReport = async () => {
    if (!client) return;

    setIsSending(true);
    try {
      const reportData: StatusReportData = {
        clientName: client.companyName,
        fiscalYear,
        quarter: quarter !== 'all' ? quarter : undefined,
        basStatus: client.basStatus,
        bookkeepingStatus: client.bookkeepingStatus,
        payrollStatus: client.payrollPayslip,
        superStatus: client.superPayment,
        invoicesStatus: client.invoices,
        remarks: client.remarks,
        generatedAt: new Date()
      };

      const result = await emailService.sendStatusReport(
        client.emailAddress,
        client.companyName,
        reportData
      );

      if (result.success) {
        toast({
          title: 'Status Report Sent',
          description: result.message || 'Status report email has been opened.',
        });
        onOpenChange(false);
      } else {
        toast({
          title: 'Status Report Failed',
          description: result.error || 'Failed to send status report.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Status Report Error',
        description: 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Status Report Email
          </DialogTitle>
          <DialogDescription>
            Send a status report to {client.companyName} via email
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Client Info */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4" />
              <span className="font-semibold">Client Information</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Company:</span>
                <p className="font-medium">{client.companyName}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Director:</span>
                <p className="font-medium">{client.directorName}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Email:</span>
                <p className="font-medium">{client.emailAddress}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Period:</span>
                <p className="font-medium">{fiscalYear} {quarter !== 'all' ? `• ${quarter}` : ''}</p>
              </div>
            </div>
          </div>

          {/* Email Form */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="to">To Email</Label>
              <Input
                id="to"
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                placeholder="recipient@example.com"
              />
            </div>

            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={emailData.subject}
                onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject"
              />
            </div>

            <div>
              <Label htmlFor="body">Message</Label>
              <Textarea
                id="body"
                value={emailData.body}
                onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Email message"
                rows={12}
                className="font-mono text-sm"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendStatusReport}
            disabled={isSending || !emailData.to}
            className="bg-primary text-primary-foreground"
          >
            {isSending ? (
              <>
                <div className="animate-spin h-4 w-4 mr-2 border-2 border-current border-t-transparent rounded-full" />
                Opening Email...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Status Report
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};